import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../utils/api';
import { format } from 'date-fns';
import { getPermLevel } from '../../utils/rolesConfig';

const ACTION_COLOR = {
  CREATE:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  UPDATE:  'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
  DELETE:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  LOGIN:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LOGOUT:  'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  APPROVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  REJECT:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  EXPORT:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  USER_LOCKED:             'bg-red-100 text-red-700',
  USER_UNLOCKED:           'bg-emerald-100 text-emerald-700',
  ADMIN_PASSWORD_RESET:    'bg-amber-100 text-amber-700',
  PASSWORD_RESET_VIA_TOKEN:'bg-amber-100 text-amber-700',
  USER_ROLE_CHANGED:       'bg-brand-100 text-brand-700',
};
const LEVEL_CLASS = {
  SystemAdmin:'bg-brand-100 text-brand-700',  Admin:'bg-blue-100 text-blue-700',
  Executive:  'bg-purple-100 text-purple-700', Manager:'bg-cyan-100 text-cyan-700',
  Developer:  'bg-indigo-100 text-indigo-700', Specialist:'bg-emerald-100 text-emerald-700',
};
const getRoleClass = (role) => LEVEL_CLASS[getPermLevel(role)] || LEVEL_CLASS.Specialist;
const Search = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const ChevL = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const ChevR = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;

export default function AdminAuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action:'', entity_type:'', from:'', to:'', search:'' });
  const [error,   setError]   = useState('');
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getAuditLogs({ ...filters, page, limit });
      setLogs(r.data.logs || []); setTotal(r.data.total || 0);
    } catch(e) { setError(e.response?.data?.message || 'Failed to load audit logs'); } finally { setLoading(false); }
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);
  const setFilter = (k, v) => { setFilters(f => ({...f, [k]: v})); setPage(1); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="page-title">Audit Logs</h1><p className="text-sm" style={{ color:'var(--text-secondary)' }}>{total} total records</p></div>
      </div>

      {/* Filters */}
      <div className="card flex flex-wrap gap-3 p-4">
        <div className="relative flex-1 min-w-44">
          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}><Search /></span>
          <input className="input pl-9 text-sm" placeholder="Search action, entity..." value={filters.search} onChange={e=>setFilter('search',e.target.value)} />
        </div>
        <select className="select w-36 text-sm" value={filters.action} onChange={e=>setFilter('action',e.target.value)}>
          <option value="">All Actions</option>
          {['LOGIN','LOGOUT','CREATE','UPDATE','DELETE','APPROVE','REJECT','EXPORT',
             'USER_LOCKED','USER_UNLOCKED','ADMIN_PASSWORD_RESET',
             'PASSWORD_RESET_VIA_TOKEN','USER_ROLE_CHANGED'].map(a=>(
            <option key={a} value={a}>{a.replace(/_/g,' ')}</option>
          ))}
        </select>
        <select className="select w-40 text-sm" value={filters.entity_type} onChange={e=>setFilter('entity_type',e.target.value)}>
          <option value="">All Entities</option>
          {[
            'auth','project','task','activity','bug','change_request',
            'training','workplan','user','client','milestone','risk',
            'notification','delegation','tag','attachment','comment','user'
          ].map(e=><option key={e} value={e}>{e.replace('_',' ')}</option>)}
        </select>
        <input type="date" className="input w-40 text-sm" value={filters.from} onChange={e=>setFilter('from',e.target.value)} />
        <input type="date" className="input w-40 text-sm" value={filters.to} onChange={e=>setFilter('to',e.target.value)} />
        <button onClick={()=>{ setFilters({ action:'', entity_type:'', from:'', to:'', search:'' }); setPage(1); }} className="btn-secondary text-sm">Clear</button>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}
      <div className="card overflow-hidden p-0">
        {loading ? <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
        : logs.length === 0 ? <p className="text-center py-10 text-sm" style={{ color:'var(--text-secondary)' }}>No logs found</p>
        : (
          <table className="w-full text-sm">
            <thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              {['Timestamp','User','Role','Action','Entity','Details'].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors" style={{ borderColor:'var(--border)' }}>
                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{format(new Date(l.created_at), 'MMM d, HH:mm:ss')}</td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium" style={{ color:'var(--text)' }}>{l.user_name || 'System'}</p>
                    {l.user_email&&<p className="text-xs" style={{ color:'var(--text-secondary)' }}>{l.user_email}</p>}
                  </td>
                  <td className="px-4 py-3">{l.user_role&&<span className={`badge text-xs ${getRoleClass(l.user_role)}`}>{l.user_role}</span>}</td>
                  <td className="px-4 py-3"><span className={`badge text-xs ${ACTION_COLOR[l.action]||'bg-slate-100 text-slate-600'}`}>{l.action}</span></td>
                  <td className="px-4 py-3 text-xs" style={{ color:'var(--text)' }}>
                    <span className="font-medium">{l.entity_type}</span>
                    {l.entity_name&&<span className="ml-1" style={{ color:'var(--text-secondary)' }}>"{l.entity_name}"</span>}
                  </td>
                  <td className="px-4 py-3 text-xs max-w-[200px] truncate" style={{ color:'var(--text-secondary)' }}>
                    {l.details ? JSON.stringify(l.details).slice(0, 60) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor:'var(--border)' }}>
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Page {page} of {totalPages} ({total} records)</p>
            <div className="flex gap-1">
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-1.5 rounded hover:bg-[var(--surface-alt)] disabled:opacity-30" style={{ color:'var(--text)' }}><ChevL /></button>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-1.5 rounded hover:bg-[var(--surface-alt)] disabled:opacity-30" style={{ color:'var(--text)' }}><ChevR /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
